import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IStyle } from 'Styles';
import { getDropScoreRank, getWellKey, handleSort } from '../utils';
import {
  dropDownSelectorKeyValueTitle,
  DropsCheckboxStateType,
  FindDropsListType,
  sortingOrder,
  WellInfoAllSortDataType,
  WellInfoItemType,
} from '../Contants';
import WellCard from './WellCard';
import WellInfoHeaderBar from './WellInfoHeaderBar';
import { useRMSelector } from 'redux/hooks';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import { ScoreDefinitionDTO } from 'business/models/Models';
import { compareWithScoreGroupId, GetScoreDefn } from 'utils/Global';
import { selectHitWells } from 'redux/selectors/StateSelectors';
import PlateService from 'business/services/PlateService';

export const sortSelectorInitialValues = (
  defaultSelectedKey: keyof typeof dropDownSelectorKeyValueTitle = null,
) => ({
  selectedValue: defaultSelectedKey,
  sortOrder: sortingOrder.ASCENDING,
});

const WellInfo = (props) => {
  const experimentData = useRMSelector(selectExp);
  const [wellInfoAllSortData, setWellInfoAllSortData] = useState<WellInfoAllSortDataType>(
    {
      0: sortSelectorInitialValues('plateNumber'),
      1: sortSelectorInitialValues('wellNumber'),
      2: sortSelectorInitialValues('dropNumber'),
    },
  );
  const [dropsCheckboxState, setDropsCheckboxState] = useState<DropsCheckboxStateType>({
    0: true,
    1: true,
    2: true,
    3: true,
    4: true,
  });
  const [dropsListAllData, setDropsListAllData] = useState<FindDropsListType>([]);
  const [wellCardList, setWellCardList] = useState<FindDropsListType>([]);
  const expHitWells = useRMSelector(selectHitWells);

  const getFindDropList = useCallback(async () => {
    const newFindDropsDataList = [];
    const wellScoresData = await getWellScoresData();
    for (const plate of experimentData?.plateDto) {
      for (const well of plate?.ingredients?.wellList) {
        if (!window?.location?.pathname.includes('/screen')) {
          for (const drop of well?.dropList) {
            newFindDropsDataList.push({
              plateId: plate.plateId,
              plateNumber: plate.plateNumber,
              wellRow: well.rowLetter,
              wellCol: well.columnNumber,
              wellNumber: well.wellNumber,
              wellId: well.wellId,
              wellLayerList: well.wellLayerList,
              dropNumber: drop.dropNumber,
              scoreOrderNumber: getDropScoreRank(
                wellScoresData,
                plate.plateId,
                well.wellNumber,
                drop.dropNumber,
              ),
              wellScores: getWellScoresList(wellScoresData, plate, well, drop),
            });
          }
        } else {
          newFindDropsDataList.push({
            plateId: plate.plateId,
            plateNumber: plate.plateNumber,
            wellRow: well.rowLetter,
            wellCol: well.columnNumber,
            wellNumber: well.wellNumber,
            wellId: well.wellId,
            wellLayerList: well.wellLayerList,
            dropNumber: '',
            scoreOrderNumber: getDropScoreRank(
              wellScoresData,
              plate.plateId,
              well.wellNumber,
              null,
            ),
            wellScores: getWellScoresList(wellScoresData, plate, well, null),
          });
        }
      }
    }
    return newFindDropsDataList;
  }, [experimentData.plateDto]);

  useEffect(() => {
    const fetch = async () => {
      const newDropsList = await getFindDropList();
      setDropsListAllData(newDropsList);
      setWellCardList(newDropsList);
    };
    fetch();
  }, [experimentData.plateDto]);

  const getWellScoresData = useCallback(async () => {
    const wellScoresData = [];
    const allScores: ScoreDefinitionDTO[] = GetScoreDefn();
    for (const plate of experimentData.plateDto) {
      const plateService = new PlateService();
      const plateScores = await plateService.getPlateScoreBasedOnWellsByPlateId(
        plate.plateId,
      );
      for (const score of plateScores) {
        for (const scoreInfo of score) {
          const scoreToPush = allScores.find(
            (x) =>
              x.wellDropScoreId === scoreInfo.wellDropScoreId &&
              x.wellDropScoreGroupId === scoreInfo.wellDropScoreGroupId,
          );
          wellScoresData.push({
            ...scoreToPush,
            wellNumber: scoreInfo.wellNumber,
            plateId: plate.plateId,
            orderNumber: scoreInfo.orderNumber,
            dropNumber: scoreInfo.dropNumber,
            wellDropScoreGroupId: scoreInfo.wellDropScoreGroupId,
            scoreBatchId: scoreInfo.scoreBatchId,
          });
        }
      }
    }
    return wellScoresData;
  }, [experimentData.plateDto]);

  const getWellScoresList = (wellScores, plate, well, drop) => {
    const scores = [...wellScores]
      ?.filter(
        (wellScore) =>
          wellScore.wellNumber === well.wellNumber &&
          wellScore.plateId === plate.plateId &&
          (drop == null || wellScore.dropNumber === drop.dropNumber),
      )
      .sort(compareWithScoreGroupId);
    const scoreNames = scores.map((item) => {
      return `${item.scoreName}(${item.scoreGroupName})`;
    });
    return scoreNames.join(', ');
  };

  const changeSorting = (sortDropDownNumber: number) => {
    const data = handleSort(sortDropDownNumber, wellCardList, wellInfoAllSortData);
    setWellCardList(data);
    setWellInfoAllSortData((prvState: WellInfoAllSortDataType) => ({
      ...prvState,
      [sortDropDownNumber]: {
        ...prvState[sortDropDownNumber],
        sortOrder:
          prvState[sortDropDownNumber].sortOrder === sortingOrder.ASCENDING
            ? sortingOrder.DESCENDING
            : sortingOrder.ASCENDING,
      },
    }));
  };

  const toggleDrops = (dropNumber: number) => {
    let newDropsList = [...wellCardList];
    const newDropsCheckboxState = {
      ...dropsCheckboxState,
      [dropNumber]: !dropsCheckboxState[dropNumber],
    };
    setDropsCheckboxState(newDropsCheckboxState);
    const allTrue = Object.values(newDropsCheckboxState).every((v) => v === true);
    const allFalse = Object.values(newDropsCheckboxState).every((v) => v === false);
    const filterDrops = Object.keys(newDropsCheckboxState).filter(function (key) {
      return newDropsCheckboxState[key] === false;
    });

    if (allFalse) {
      setWellCardList([]);
      return;
    } else if (allTrue) {
      setWellCardList(dropsListAllData);
      return;
    }
    if (!newDropsList.length) {
      newDropsList = [...dropsListAllData];
    }
    if (filterDrops?.length) {
      newDropsList = dropsListAllData;
      for (const drop of filterDrops) {
        newDropsList = newDropsList.filter((d) => d.dropNumber != Number(drop) + 1);
      }
      setWellCardList(newDropsList);
    }
  };

  const wellCardsList = useMemo(() => {
    let filteredList = wellCardList;
    if (expHitWells != null) {
      const wellLevelKeys = new Set<string>();
      const dropLevelKeys = new Set<string>();
      expHitWells.forEach((expWell) => {
        if (expWell.dropNumber == null) {
          wellLevelKeys.add(getWellKey(expWell));
        } else {
          dropLevelKeys.add(`${getWellKey(expWell)}-${expWell.dropNumber}`);
        }
      });
      filteredList = wellCardList.filter((well) => {
        const wellKey = getWellKey({
          plateId: Number(well.plateId),
          wellNumber: Number(well.wellNumber),
        });
        return (
          wellLevelKeys.has(wellKey) || dropLevelKeys.has(`${wellKey}-${well.dropNumber}`)
        );
      });
    }
    return filteredList?.map((wellInfoItem: WellInfoItemType) => (
      <WellCard key={`wellCardd${wellInfoItem.id}`} wellInfoItem={wellInfoItem} />
    ));
  }, [wellCardList, expHitWells]);

  return (
    <div id={props.id} style={styles.root}>
      <WellInfoHeaderBar
        dropsCheckboxState={dropsCheckboxState}
        wellInfoAllSortData={wellInfoAllSortData}
        totalDrops={
          experimentData?.plateDto[0]?.ingredients?.containerDropList?.length || 2
        }
        setWellInfoAllSortData={setWellInfoAllSortData}
        changeSorting={changeSorting}
        toggleDrops={toggleDrops}
      />
      <div id="exp-finddrop-wellinfo-index-cardlist" style={styles.wellInfoContentStyle}>
        {wellCardsList}
      </div>
    </div>
  );
};

const styles: IStyle = {
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    width: '100%',
  },
  wellInfoContentStyle: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    width: '100%',
  },
};

export default WellInfo;
